import { MIME_TYPES } from "@excalidraw/common";
import { decompressData } from "@excalidraw/excalidraw/data/encode";
import { getSceneVersion } from "@excalidraw/element";

import type { ExcalidrawElement, FileId } from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFileData,
  BinaryFileMetadata,
  DataURL,
} from "@excalidraw/excalidraw/types";

import { WS_SUBTYPES } from "../app_constants";

import type Portal from "../collab/Portal";
import type { StagsCollabSocket } from "../collab/StagsCollabSocket";
import type { SyncableExcalidrawElement } from ".";

const sceneVersions = new WeakMap<StagsCollabSocket, number>();

const getHttpBaseUrl = () => {
  const configured = import.meta.env.VITE_APP_WS_SERVER_URL;
  if (!configured) {
    throw new Error("Stags collaboration service is not configured");
  }
  const url = new URL(configured);
  if (url.protocol === "wss:") {
    url.protocol = "https:";
  } else if (url.protocol === "ws:") {
    url.protocol = "http:";
  } else if (url.protocol !== "https:" && url.protocol !== "http:") {
    throw new Error("Stags collaboration URL must use HTTP(S) or WS(S)");
  }
  url.pathname = url.pathname.replace(/\/$/, "");
  url.search = "";
  url.hash = "";
  return url;
};

const roomIdFromPrefix = (prefix: string) => {
  const parts = prefix.replace(/^\/+|\/+$/g, "").split("/");
  const namespace = parts.at(-2);
  const id = parts.at(-1);
  if (!id || (namespace !== "rooms" && namespace !== "shareLinks")) {
    throw new Error("Invalid Stags Canvas storage prefix");
  }
  const roomId = namespace === "shareLinks" ? `share_${id}` : id;
  if (!/^[A-Za-z0-9_-]{1,128}$/.test(roomId)) {
    throw new Error("Invalid Stags Canvas storage room id");
  }
  return roomId;
};

const fileUrl = (prefix: string, fileId: FileId) => {
  if (!/^[A-Za-z0-9_-]{1,128}$/.test(fileId)) {
    throw new Error("Invalid Stags Canvas file id");
  }
  const url = getHttpBaseUrl();
  url.pathname = `${url.pathname}/room/${roomIdFromPrefix(
    prefix,
  )}/files/${fileId}`;
  return url.toString();
};

export const isSavedToFirebase = (
  portal: Portal,
  elements: readonly ExcalidrawElement[],
) => {
  if (!portal.socket || !portal.roomId || !portal.roomKey) {
    return true;
  }
  return sceneVersions.get(portal.socket) === getSceneVersion(elements);
};

export const saveToFirebase = async (
  portal: Portal,
  elements: readonly SyncableExcalidrawElement[],
  appState: AppState,
) => {
  void appState;
  if (
    !portal.socket ||
    !portal.roomId ||
    !portal.roomKey ||
    !portal.isOpen() ||
    isSavedToFirebase(portal, elements)
  ) {
    return null;
  }

  await portal.broadcastScene(WS_SUBTYPES.UPDATE, elements, true);
  sceneVersions.set(portal.socket, getSceneVersion(elements));
  return null;
};

export const loadFromFirebase = async (
  roomId: string,
  roomKey: string,
  socket: StagsCollabSocket | null,
): Promise<readonly SyncableExcalidrawElement[] | null> => {
  void roomId;
  void roomKey;
  void socket;
  return null;
};

export const saveFilesToFirebase = async ({
  prefix,
  files,
}: {
  prefix: string;
  files: { id: FileId; buffer: Uint8Array }[];
}) => {
  const erroredFiles: FileId[] = [];
  const savedFiles: FileId[] = [];

  await Promise.all(
    files.map(async ({ id, buffer }) => {
      try {
        const response = await fetch(fileUrl(prefix, id), {
          method: "PUT",
          headers: { "Content-Type": "application/octet-stream" },
          body: new Uint8Array(buffer).buffer,
        });
        if (!response.ok) {
          throw new Error(`File upload failed with HTTP ${response.status}`);
        }
        savedFiles.push(id);
      } catch (error) {
        erroredFiles.push(id);
        console.error("Stags Canvas file upload failed", error);
      }
    }),
  );

  return { savedFiles, erroredFiles };
};

export const loadFilesFromFirebase = async (
  prefix: string,
  decryptionKey: string,
  fileIds: readonly FileId[],
) => {
  const loadedFiles: BinaryFileData[] = [];
  const erroredFiles = new Map<FileId, true>();

  await Promise.all(
    [...new Set(fileIds)].map(async (id) => {
      try {
        const response = await fetch(fileUrl(prefix, id));
        if (!response.ok) {
          throw new Error(`File download failed with HTTP ${response.status}`);
        }
        const { data, metadata } = await decompressData<BinaryFileMetadata>(
          new Uint8Array(await response.arrayBuffer()),
          { decryptionKey },
        );
        const dataURL = new TextDecoder().decode(data) as DataURL;
        loadedFiles.push({
          mimeType: metadata.mimeType || MIME_TYPES.binary,
          id,
          dataURL,
          created: metadata.created || Date.now(),
          lastRetrieved: metadata.created || Date.now(),
        });
      } catch (error) {
        erroredFiles.set(id, true);
        console.error("Stags Canvas file download failed", error);
      }
    }),
  );

  return { loadedFiles, erroredFiles };
};
